import { Col, Row } from 'react-bootstrap';
import StepTabs from '../../../components/StepTabs';
import PropTypes from 'prop-types';
import { useMyContentContext } from '../utils/myContentContext';
import {
  useSaveCRQuestionMutation,
  useSaveMcFillInQuestionMutation,
  useUpdateCRQuestionMutation,
  useUpdateMcFillInQuestionMutation,
  useUpdateHintMutation,
  useUpdateReasonMutation,
  useUpdateAnswerOptionsMutation,
  useUpdatePropertiesMutation,
  useUpdateCRAnswerMutation,
  useUpdateCROptionsMutation,
  useUpdateCRPropertiesMutation
} from '../../../features/authentication/qSetApiSlice';
import QuestionTypes from '../../../enums/questionTypes';
import CustomToast from '../../../components/Toast';
import CreateQuestionSetModel from '../pages/questionSets/components/CreateQuestionSetModel';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { bytesToBase64 } from '../../../utils/pdfUtils';
import { UrlFormatter } from '../../../utils/urlFormatter';

const QuestionEditorHeader = ({
  activeStep,
  setActiveStep,
  isCRQuestion
}) => {
  const {
    question,
    hint,
    reason,
    questionType,
    fillInAnswer,
    fillInAnswerBoxWidth,
    fillInAnswerBoxFontSize,
    correctAnswer,
    numChoices,
    selectedAttributeNodes,
    selectedTagNodes,
    selectedStandardNodes,
    selectedTopicNodes,
    questionDocuments,
    answerSize,
    sampleAnswer,
    editAnswer,
    maxPoints,
    options,
    crAnswerToolId
  } = useMyContentContext();

  const steps = isCRQuestion ?
    [
      { label: 'Question' },
      { label: 'Sample Answer' },
      { label: 'Options' },
      { label: 'Topics/Tags/<br/>Attributes/Standards' }
    ] :
    [
      { label: 'Question' },
      { label: 'Hint' },
      { label: 'Reason' },
      { label: 'Answer Options' },
      { label: 'Topics/Tags/<br/>Attributes/Standards' }
    ];

  const { setId, questionId } = useParams();

  const [saveMcFillInQuestion] = useSaveMcFillInQuestionMutation();
  const [updateMcFillInQuestion] = useUpdateMcFillInQuestionMutation();
  const [updateHint] = useUpdateHintMutation();
  const [updateReason] = useUpdateReasonMutation();
  const [updateAnswerOptions] = useUpdateAnswerOptionsMutation();
  const [updateProperties] = useUpdatePropertiesMutation();

  const [saveCRQuestion] = useSaveCRQuestionMutation();
  const [updateCRQuestion] = useUpdateCRQuestionMutation();
  const [updateCRAnswer] = useUpdateCRAnswerMutation();
  const [updateCROptions] = useUpdateCROptionsMutation();
  const [updateCRProperties] = useUpdateCRPropertiesMutation();

  const [openCreateQuestion, setOpenCreateQuestion] = useState(false);
  const navigate = useNavigate();

  const handleStepClick = (step) => {
    if (activeStep == 1) {
      isCRQuestion ? handleCRQuestionSave(setId) : handleMcFillInQuestionSave(setId);
    }
    else {
      setActiveStep(step);
    }
  };

  const handleMcFillInSave = (setId) => {
    switch (activeStep) {
    case 1: {
      handleMcFillInQuestionSave(setId);
      break;
    }
    case 2: {
      handleHintSave();
      break;
    }
    case 3: {
      handleReasonSave();
      break;
    }
    case 4: {
      handleAnswerOptionSave();
      break;
    }
    case 5: {
      handlePropertiesSave();
    }
    }
  };

  const handleMcFillInQuestionSave = async (setId) => {
    const error = validateMcFillInQuestion();
    if (error.length > 0) {
      CustomToast(error, 'error');
      return;
    }
    if (!setId && activeStep + 1 > 1)
      setOpenCreateQuestion(true);

    if (setId) {
      if (questionId) {
        const option = {
          questionId: questionId,
          question: `"${bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(question)))}"`
        };
        const response = await updateMcFillInQuestion(option);
        if (response.error) {
          if (response.error.data.errors)
            CustomToast(response.error.data.errors.Error, 'error');
          else
            CustomToast(response.error.data.error, 'error');
        }
      }
      else {
        let option = {
          id: setId,
          params: {
            question: bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(question))),
            qType: questionType,
            documents: questionDocuments.map((obj) => { return { id: obj.documentId, displayWithQuestion: obj.displayWithQuestion }; })
          }
        };
        const response = await saveMcFillInQuestion(option);
        if (response.error) {
          if (response.error.data.errors)
            CustomToast(response.error.data.errors.Error, 'error');
          else
            CustomToast(response.error.data.error, 'error');
        } else {
          CustomToast('Question added successfully', 'success');
          navigate(`/mycontent/questionsets/${setId}/mc_fillInQuestion/${response.data.questionId}/-1`);
        }
      }
      setActiveStep(activeStep + 1);
    }
  };

  const handleHintSave = async () => {
    if (questionId) {
      const option = {
        questionId: questionId,
        hint: `"${bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(hint)))}"`
      };
      const response = await updateHint(option);
      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.Error, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  };

  const handleReasonSave = async () => {
    const option = {
      questionId: questionId,
      reason: `"${bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(reason)))}"`
    };
    const response = await updateReason(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleAnswerOptionSave = async () => {
    const error = validateMcFillInQuestion();
    if (error.length > 0) {
      CustomToast(error, 'error');
      return;
    }
    const option = {
      questionId: questionId,
      params: {
        choices: numChoices,
        correctAnswer: correctAnswer,
        fillInAnswer: fillInAnswer,
        fillInAnswerFontSize: fillInAnswerBoxFontSize,
        fillInAnswerWidth: fillInAnswerBoxWidth
      }
    };
    const response = await updateAnswerOptions(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handlePropertiesSave = async () => {
    const option = {
      questionId: questionId,
      params: {
        tags: selectedTagNodes,
        topics: toValueNameModel(selectedTopicNodes),
        attributes: selectedAttributeNodes,
        standards: selectedStandardNodes
      }
    };
    const response = isCRQuestion ? await updateCRProperties(option) : await updateProperties(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      setActiveStep(activeStep + 1);
      navigate(`/mycontent/questionsets/${setId}/questions`);
    }
  };


  const validateMcFillInQuestion = () => {
    const cleanQ = question.replace(/&nbsp;/g, '').replace(/ /g, '');
    const emptyQuestion = !cleanQ || cleanQ.trim() === '' || cleanQ.indexOf('<p></p>') === 0;
    if (emptyQuestion) {
      return 'The question field is cannot be blank.';
    }

    if (questionType === QuestionTypes.FillIn) {
      if (activeStep == 1 || activeStep == 4) {
        let count = 0;
        let offset = 0;
        let questionString = question;
        while ((offset = (' ' + questionString).indexOf('~~1~~', offset + 1)) !== -1) {
          count++;
          if (count > 1) break;
        }
        if (count < 1) {
          questionString += '<p>Answer: ~~1~~</p>';
          return 'No answer field was specified, so one has been appended to the end of the question. An answer field is specified by including ~~1~~ in the question.';
        } else if (count > 1) {
          return 'You can only specify one answer field. Please remove additional ~~1~~ strings from the question.';
        }
      }

      if (fillInAnswer.length == 0 && activeStep == 3) {
        return 'The fill-in answer cannot be blank.';
      }
    }
    else if (questionType == QuestionTypes.MC) {
      const quesLower = (question || '').toLowerCase();

      if ((activeStep == 1 || activeStep == 4) && !(quesLower.includes('<ol') && quesLower.includes('<li')) && !(quesLower.includes('<ul') && quesLower.includes('<li'))) {
        return 'No multiple-choice answers were created inside the question text. Please create answer choices by using the tool.';
      }

      if (activeStep == 4) {
        let count = 0;
        let offset = 0;
        while ((offset = (' ' + quesLower).indexOf('<li', offset + 1)) !== -1) {
          count++;
        }

        if (count !== numChoices) {
          return 'The number of answer choices in the question text does not equal the number specified under \'Number of Choices:\' <br/> Please try again.';
        }

        if (correctAnswer > numChoices) {
          return 'The multiple choice answer cannot be a larger number than the number of choices.';
        }
      }
    }
    return '';
  };

  const handleCRSave = async (setId) => {
    switch (activeStep) {
    case 1: {
      handleCRQuestionSave(setId);
      break;
    }
    case 2: {
      handleCRAnswerSave();
      break;
    }
    case 3: {
      handleCROptionSave();
      break;
    }
    case 4: {
      handlePropertiesSave();
      break;
    }
    }
  };

  const validateCRQuestion = () => {
    const cleanQ = question.replace(/&nbsp;/g, '').replace(/ /g, '');
    const emptyQuestion = !cleanQ || cleanQ.trim() === '' || cleanQ.indexOf('<p></p>') === 0;
    if (emptyQuestion) {
      return 'The question field is cannot be blank.';
    }
    return '';
  };

  const handleCRQuestionSave = async (setId) => {
    const error = validateCRQuestion();
    if (error.length > 0) {
      CustomToast(error, 'error');
      return;
    }

    if (!setId && activeStep + 1 > 1)
      setOpenCreateQuestion(true);

    if (setId) {
      if (questionId) {
        const option = {
          questionId: questionId,
          question: `"${bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(question)))}"`
        };
        const response = await updateCRQuestion(option);
        if (response.error) {
          if (response.error.data.errors)
            CustomToast(response.error.data.errors.Error, 'error');
          else
            CustomToast(response.error.data.error, 'error');
        }
      }
      else {
        let option = {
          id: setId,
          params: {
            question: bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(question))),
            documents: questionDocuments.map((obj) => { return { id: obj.documentId, displayWithQuestion: obj.displayWithQuestion }; })
          }
        };
        const response = await saveCRQuestion(option);
        if (response.error) {
          if (response.error.data.errors)
            CustomToast(response.error.data.errors.Description, 'error');
          else
            CustomToast(response.error.data.error, 'error');
        } else {
          CustomToast('Question added successfully', 'success');
          navigate(`/mycontent/questionsets/${setId}/cr_Question/${response.data.questionId}/-2`);
        }
      }
      setActiveStep(activeStep + 1);
    }
  };

  const handleCRAnswerSave = async () => {
    if (questionId) {
      const option = {
        questionId: questionId,
        answer: `"${bytesToBase64(new TextEncoder().encode(UrlFormatter.ReplaceBaseUrlWithDots(sampleAnswer)))}"`
      };
      const response = await updateCRAnswer(option);
      if (response.error) {
        if (response.error.data.errors)
          CustomToast(response.error.data.errors.Error, 'error');
        else
          CustomToast(response.error.data.error, 'error');
      } else {
        setActiveStep(activeStep + 1);
      }
    }
  };

  const handleCROptionSave = async () => {
    const option = {
      questionId: questionId,
      params: {
        answerSize: answerSize,
        maxPoints: maxPoints,
        options: options,
        editAnswer: editAnswer,
        crAnswerToolId: crAnswerToolId
      }
    };
    const response = await updateCROptions(option);
    if (response.error) {
      if (response.error.data.errors)
        CustomToast(response.error.data.errors.Error, 'error');
      else
        CustomToast(response.error.data.error, 'error');
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const toValueNameModel = (topics) => {
    const topicArray = topics.map(({ id, name }) => ({
      value: id,
      name
    }));
    return topicArray;
  };

  return (
    <>
      <Row className='align-items-center d-flex justify-content-between p-4 pb-5'>
        <Col className='col-2'><h1 className=''>Question Editor</h1></Col>
        <Col className='col-5 mb-4'>
          <StepTabs
            steps={steps}
            activeStep={activeStep}
            onClick={handleStepClick} />
        </Col>
        {((isCRQuestion && activeStep == 5) || (isCRQuestion && activeStep == 4)) ? (
          <Col className='d-flex justify-content-end col-3 gap-3'>
            <button id="back_button" className="btn btn-castleWhite btn-width" onClick={() => window.history.back()}>
              Cancel
            </button>
            <button id="save_button" className="btn btn-castleRed btn-width" onClick={() => isCRQuestion ? handleCRSave(setId) : handleMcFillInSave(setId)}>
              Save
            </button>
          </Col>
        ) : (
          <Col className='d-flex justify-content-end col-2'>
            <button id="save_button" className="btn btn-castleRed" onClick={() => isCRQuestion ? handleCRSave(setId) : handleMcFillInSave(setId)}>
              Save and Continue
            </button>
          </Col>
        )}
      </Row>
      <CreateQuestionSetModel
        isOpen={openCreateQuestion}
        onClose={() => setOpenCreateQuestion(false)}
        showQuestionOptions={false}
        onCreateSuccess={isCRQuestion ? handleCRQuestionSave : handleMcFillInQuestionSave}
      />
    </>
  );
};

QuestionEditorHeader.propTypes = {
  activeStep: PropTypes.number,
  setActiveStep: PropTypes.func,
  isCRQuestion: PropTypes.bool
};

export default QuestionEditorHeader;